import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDashboardData } from "./hooks/useDashboardData";
import { useBusinessType } from "./hooks/useBusinessType";
import DashboardLayout from "./DashboardLayout";
import OrderHistory from "./order-history/OrderHistory";
import SetBarChart from "./set-bar-chart/SetBarChart";
import StatsOverview from "./StatsOverview";
import GenerateDailyReport from "./GenerateReport";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import TopProducts from "./insights/Topproducts";
import TopCustomers from "./insights/TopCustomers";
import TopOrders from "./insights/Toporders";
import StaffPerformance from "./StaffPerformance";
import { useUser } from "src/shared/context/UserContext";
import { RangePreset } from "./utils/dateRange";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { Users, LayoutDashboard, BarChart3, TrendingUp, LucideIcon } from "lucide-react";
import DateFilterBar from "./DateFilterBar";

type TabType = "overview" | "analytics" | "top-insights" | "staff";

interface TabConfig {
  id: TabType;
  label: string;
  icon: LucideIcon;
  salonOnly?: boolean;
}

interface TabFilterState {
  preset: RangePreset;
  startDate: string;
  endDate: string;
}

const TABS: TabConfig[] = [
  { id: "overview", label: "Overview & Sales", icon: LayoutDashboard },
  { id: "staff", label: "Staff Performance", icon: Users, salonOnly: true },
  { id: "analytics", label: "Analytics & Charts", icon: BarChart3 },
  { id: "top-insights", label: "Top Insights", icon: TrendingUp },
];

const formatLocalDate = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [shouldFetchYearData, setShouldFetchYearData] = useState(false);

  const dashboardRef = useRef<HTMLDivElement>(null);
  const previousLoadingRef = useRef(false);
  const restoreScrollTimeoutRef = useRef<NodeJS.Timeout>();

  const { user } = useUser();
  const { sidebarThemeClass, bgClass, fontClass, borderHighlight } = useUserTheme();
  const businessType = useBusinessType();
  const isSalonBusiness = businessType?.toLowerCase() === "saloon";

  const now = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => formatLocalDate(now), [now]);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Independent filter state per tab
  const [tabFilters, setTabFilters] = useState<Record<TabType, TabFilterState>>({
    overview: { preset: "Today", startDate: todayStr, endDate: todayStr },
    analytics: { preset: "Today", startDate: todayStr, endDate: todayStr },
    "top-insights": { preset: "Today", startDate: todayStr, endDate: todayStr },
    staff: { preset: "Today", startDate: todayStr, endDate: todayStr },
  });

  const currentFilter = tabFilters[activeTab];

  /** RANGE DATA */
  const { dashboardData: rangeData, loading: rangeLoading } = useDashboardData({
    mode: "range",
    startDate: currentFilter.startDate,
    endDate: currentFilter.endDate,
  });

  /** YEAR DATA */
  const { dashboardData: yearlyData, loading: yearLoading } = useDashboardData({
    mode: "year",
    year: selectedYear,
    enabled: shouldFetchYearData,
  });

  const saveScrollPosition = useCallback(() => {
    if (dashboardRef.current) {
      setScrollPosition(dashboardRef.current.scrollTop);
    }
  }, []);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === "analytics" && !shouldFetchYearData) {
      setShouldFetchYearData(true);
    }
  };

  const handleRangeChange = (start: string, end: string, nextPreset?: RangePreset) => {
    saveScrollPosition();
    setTabFilters((prev) => ({
      ...prev,
      [activeTab]: {
        startDate: start,
        endDate: end,
        preset: nextPreset ?? prev[activeTab].preset,
      },
    }));
  };

  const handleYearChange = (increment: number) => {
    saveScrollPosition();
    const currentYear = new Date().getFullYear();
    setSelectedYear((prev) => {
      const newYear = prev + increment;
      const validYear = newYear <= currentYear ? newYear : prev;
      if (validYear !== prev) setShouldFetchYearData(true);
      return validYear;
    });
  };

  // Scroll Restoration Logic
  useEffect(() => {
    const isCurrentlyLoading = yearLoading || rangeLoading;

    if (previousLoadingRef.current && !isCurrentlyLoading) {
      setIsRestoringScroll(true);
      if (restoreScrollTimeoutRef.current) clearTimeout(restoreScrollTimeoutRef.current);

      restoreScrollTimeoutRef.current = setTimeout(() => {
        if (dashboardRef.current && scrollPosition > 0) {
          dashboardRef.current.scrollTop = scrollPosition;
          setIsRestoringScroll(false);
        }
      }, 100);
    }

    previousLoadingRef.current = isCurrentlyLoading;

    return () => {
      if (restoreScrollTimeoutRef.current) clearTimeout(restoreScrollTimeoutRef.current);
    };
  }, [yearLoading, rangeLoading, scrollPosition]);

  useEffect(() => {
    const handleScroll = () => {
      if (dashboardRef.current && !yearLoading && !rangeLoading && !isRestoringScroll) {
        setScrollPosition(dashboardRef.current.scrollTop);
      }
    };

    const scrollElement = dashboardRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);
      return () => scrollElement.removeEventListener("scroll", handleScroll);
    }
  }, [yearLoading, rangeLoading, isRestoringScroll]);

  const staffSummary = rangeData?.staff_summary ?? [];
  const visibleTabs = TABS.filter((tab) => !tab.salonOnly || isSalonBusiness);

  return (
    <DashboardLayout businessType={businessType}>
      <div ref={dashboardRef} className="thin-scrollbar p-1 w-full h-[92vh] overflow-y-auto">
        <div className={`shadow-inner rounded-lg ${sidebarThemeClass}`}>
          {/* Header */}
          <div className="p-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-100">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
              <p className="text-gray-600 mt-1">Welcome back! Here's what's happening.</p>
            </div>

            {user?.businessType !== "Grocery" && (
              <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
                {reportGenerated && (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2 max-w-md">
                    The report has been sent to your email. For date-wise reports, please visit the Report tab to view or download them.
                  </div>
                )}
                <GenerateDailyReport onReportGenerated={() => setReportGenerated(true)} />
              </div>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className={`px-2 pt-2 pb-1 ${borderHighlight}`}>
            <div className={`flex gap-1 p-1 rounded-xl bg-white ${borderHighlight}`}>
              {visibleTabs.map(({ id, label, icon: Icon }) => {
                const isActive = activeTab === id;
                return (
                  <button
                    key={id}
                    onClick={() => handleTabChange(id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold text-base transition-all duration-200 ${
                      isActive ? `${bgClass} ${fontClass} shadow-sm ring-1 ring-slate-200/80` : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/50"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? "text-inherit" : "text-slate-400"}`} />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Body */}
          <div className="px-4 pt-1.5 pb-4 flex flex-col gap-2.5">
            {rangeLoading ? (
              <div className="min-h-[300px] flex items-center justify-center">
                <LoadingScreen message="Please wait, Loading Dashboard..." />
              </div>
            ) : (
              <div>
                {/* SECTION 1: OVERVIEW & SALES */}
                {activeTab === "overview" && rangeData && (
                  <div className="flex flex-col gap-3">
                    <StatsOverview dashboardData={rangeData} preset={currentFilter.preset} startDate={currentFilter.startDate} endDate={currentFilter.endDate} onRangeChange={handleRangeChange} />
                    <OrderHistory dashboardData={rangeData} businessType={businessType} />
                  </div>
                )}

                {/* SECTION 2: STAFF PERFORMANCE */}
                {isSalonBusiness && activeTab === "staff" && (
                  <div className="flex flex-col gap-2.5">
                    <DateFilterBar preset={currentFilter.preset} startDate={currentFilter.startDate} endDate={currentFilter.endDate} onRangeChange={handleRangeChange} />
                    <div className="flex flex-col gap-3">
                      {staffSummary.length > 0 ? (
                        <StaffPerformance staffData={staffSummary} />
                      ) : (
                        <div className="w-full rounded-2xl border border-slate-300 bg-white p-8 text-center text-slate-500">No staff data available for the selected period.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* SECTION 3: ANALYTICS & CHARTS */}
                {activeTab === "analytics" && (
                  <div className="flex flex-col gap-3">
                    {yearLoading ? (
                      <div className="min-h-[200px] flex items-center justify-center">
                        <LoadingScreen message="Loading yearly charts..." />
                      </div>
                    ) : (
                      yearlyData && <SetBarChart dashboardData={yearlyData} selectedYear={selectedYear} onYearChange={handleYearChange} />
                    )}
                  </div>
                )}

                {/* SECTION 4: TOP INSIGHTS */}
                {activeTab === "top-insights" && rangeData && (
                  <div className="flex flex-col gap-2.5">
                    <DateFilterBar preset={currentFilter.preset} startDate={currentFilter.startDate} endDate={currentFilter.endDate} onRangeChange={handleRangeChange} />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      <TopProducts products={rangeData.top_products ?? []} />
                      <TopCustomers customers={rangeData.top_customers ?? []} />
                      <TopOrders orders={rangeData.top_orders ?? []} businessType={businessType} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
